import { patientDisplayName } from 'utils/orders';
import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, ListItemIcon, ListItemText, Backdrop, CircularProgress } from '@mui/material';
import { OrderItemEvent, OrderItemEventType } from 'models/order-item-events';
import { Order } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { InvoicingState, OrderStatus } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import AddIcon from '@mui/icons-material/Add';
import SensorsIcon from '@mui/icons-material/Sensors';
import { Sitemap } from 'mdi-material-ui';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseIcon from '@mui/icons-material/Pause';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
    buildOrderPdfFileName,
    downloadOrderPdf,
} from 'components/order-details/main-order-view/document-print-actions/files';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { isCustomer, isSupplier } from 'utils/client-settings';

type OrderOperationContextMenuProps = {
    order?: Order;
    onOrderItemEvent: (event: OrderItemEvent) => void;
};
const OrderOperationContextMenu: FC<OrderOperationContextMenuProps> = ({
    order,
    onOrderItemEvent,
}: OrderOperationContextMenuProps) => {
    const { t: translate } = useTranslation(['orders', 'workflow']);
    const [spinner, setSpinner] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();

    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );

    const modifyLocked = () => {
        return order?.isProductionFinished || order?.invoicingState !== InvoicingState.NOT_READY;
    };

    const handleContextMenuClick = (eventType: OrderItemEventType) => {
        if (order) {
            onOrderItemEvent({ order: order, eventType: eventType });
        }
    };

    const handleDownloadOrderSummaryPdf = async (type: string, order: Order) => {
        setSpinner(true);
        try {
            const currentDate = new Date().toLocaleDateString();
            // A patient-less order (DD-3494) omits the patient segment from the file name.
            const fileNameParts = [
                order.orderId,
                order.internalOrderId,
                patientDisplayName(order.patient) || undefined,
                currentDate,
            ].filter(Boolean);
            const fileName = `${fileNameParts.join('_')}.pdf`;
            await downloadOrderPdf(order._id, type, fileName);
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner(false);
            setError(null);
        }
    };

    const handleOrderSummaryPdf = async (type: string, order?: Order) => {
        if (order) {
            await handleDownloadOrderSummaryPdf(type, order);
        }
    };

    return (
        <Fragment>
            {spinner ? (
                <Backdrop
                    open={spinner}
                    sx={{ position: 'absolute', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                >
                    <CircularProgress sx={{ zIndex: 10000, position: 'absolute' }} size={60} />
                </Backdrop>
            ) : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <MenuItem onClick={() => handleOrderSummaryPdf('ORDER_SUMMARY_PDF', order)}>
                <ListItemIcon>
                    <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{translate('orders:PRINT_ORDER_SUMMARY')}</ListItemText>
            </MenuItem>
            {permissions.isInternalTechnician(currentUser.role) && !modifyLocked() ? (
                <MenuItem onClick={() => handleContextMenuClick(OrderItemEventType.MANAGE_INTERNAL_ORDER_ID)}>
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('orders:EDIT_INTERNAL_ORDER_ID')}</ListItemText>
                </MenuItem>
            ) : undefined}

            {isLabWorkflowEnabled &&
            permissions.isLabTechnician(currentUser.type) &&
            !order?.isImportedOrder &&
            order?.status === OrderStatus.ACCEPTED_BY_LAB ? (
                <MenuItem onClick={() => handleContextMenuClick(OrderItemEventType.MANAGE_WORKFLOW)}>
                    <ListItemIcon>
                        <Sitemap fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('workflow:SHOW_WORKFLOW_ACTIONS')}</ListItemText>
                </MenuItem>
            ) : undefined}
            {isLabWorkflowEnabled &&
            permissions.isLabTechnician(currentUser.type) &&
            !order?.isImportedOrder &&
            order?.status === OrderStatus.ACCEPTED_BY_LAB &&
            order?.workFlow?.currentStep?.stepId ? (
                <MenuItem
                    disabled={!!order?.workFlow?.currentStep?.workStarted}
                    onClick={() => handleContextMenuClick(OrderItemEventType.START_WORK)}
                >
                    <ListItemIcon>
                        <PlayCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('workflow:START_WORK')}</ListItemText>
                </MenuItem>
            ) : undefined}
            {isLabWorkflowEnabled &&
            permissions.isLabTechnician(currentUser.type) &&
            !order?.isImportedOrder &&
            order?.status === OrderStatus.ACCEPTED_BY_LAB &&
            order?.workFlow?.currentStep?.stepId ? (
                <MenuItem onClick={() => handleContextMenuClick(OrderItemEventType.TOGGLE_PAUSE_STEP)}>
                    <ListItemIcon>
                        {order?.workFlow?.currentStep?.isPaused ? (
                            <PlayArrowIcon fontSize="small" />
                        ) : (
                            <PauseIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {order?.workFlow?.currentStep?.isPaused
                            ? translate('workflow:UN_PAUSE_STEP')
                            : translate('workflow:PAUSE_STEP')}
                    </ListItemText>
                </MenuItem>
            ) : undefined}
            {isExternalLabUsersEnabled &&
            isSupplier(currentOrganization._id, order?.supplier._id as string) &&
            permissions.isInternalTechnician(currentUser.role) &&
            !order?.isImportedOrder ? (
                <MenuItem onClick={() => handleContextMenuClick(OrderItemEventType.MANAGE_EXTERNAL_LAB_USERS)}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('MANAGE_EXTERNAL_USERS')}</ListItemText>
                </MenuItem>
            ) : undefined}
            {isCustomer(currentOrganization._id, order?.customer._id as string) && !order?.isImportedOrder ? (
                <MenuItem onClick={() => handleContextMenuClick(OrderItemEventType.MANAGE_LINKED_DENTISTS)}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('MANAGE_PARTICIPANTS')}</ListItemText>
                </MenuItem>
            ) : undefined}
            <MenuItem
                disabled={!order?.hasOrderFiles}
                onClick={() => handleContextMenuClick(OrderItemEventType.MANAGE_DOWNLOAD_FILES)}
            >
                <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{translate('orders:DOWNLOAD_FILES')}</ListItemText>
            </MenuItem>
        </Fragment>
    );
};

export default OrderOperationContextMenu;
